/**
 * Plaid API client for brokerage integration.
 *
 * Handles communication with the backend Plaid endpoints for:
 * - Creating link tokens for Plaid Link initialization
 * - Exchanging public tokens for access tokens
 * - Fetching investment holdings
 *
 * Security: Requests to protected endpoints (snapshots, disconnect) are signed
 * with HMAC-SHA256 using a device secret stored locally.
 */

import axios from 'axios';
import { backendUrl } from '../constants';
import { getOrCreateDeviceSecret, createSignedHeaders } from '../utils/plaidSecurity';

const api = axios.create({
  baseURL: backendUrl,
});

export interface PlaidHolding {
  accountId: string;
  symbol: string;
  name: string;
  type: string;
  quantity: number;
  costBasis: number | null;
  currentValue: number;
  price: number;
  gainLoss: number | null;
  gainLossPercent: number | null;
}

export interface PlaidAccount {
  accountId: string;
  name: string;
  type: string;
  subtype: string | null;
  mask: string;
}

export interface PlaidHoldingsResponse {
  holdings: PlaidHolding[];
  accounts: PlaidAccount[];
  totalValue: number;
  unsupportedSymbols: string[];
}

// Raw API response interfaces for type safety
interface PlaidHoldingRaw {
  accountId: string;
  symbol: string;
  name: string;
  type: string;
  quantity: number;
  costBasis: number | null;
  currentValue: number;
  price: number;
  gainLoss: number | null;
  gainLossPercent: number | null;
}

interface PlaidAccountRaw {
  accountId: string;
  name: string;
  type: string;
  subtype: string | null;
  mask: string;
}

interface PlaidHoldingsRawResponse {
  holdings: PlaidHoldingRaw[];
  accounts: PlaidAccountRaw[];
  totalValue: number;
  unsupportedSymbols?: string[];
}

export interface PlaidExchangeResponse {
  itemId: string;
  institutionName: string;
}

/**
 * Create a Plaid Link token for initializing Plaid Link.
 *
 * @param clientUserId - A unique identifier for the user (device UUID)
 * @returns The link token for Plaid Link initialization
 */
export const createPlaidLinkToken = async (clientUserId: string): Promise<string> => {
  const response = await api.post('/api/plaid/link-token/', {
    clientUserId: clientUserId, // Use camelCase for DRF CamelCaseJSONParser
  });

  // Check for error in response body
  if (response.data.error) {
    console.error('[Plaid API] Server error:', response.data.error, response.data.details);
    const error = new Error(response.data.details || response.data.error);
    // Attach response data for better error handling
    (error as any).response = { data: response.data };
    throw error;
  }

  // Backend transforms snake_case to camelCase, so check both
  const linkToken = response.data.linkToken || response.data.link_token;
  if (!linkToken) {
    console.error('[Plaid API] No link_token in response:', response.data);
    throw new Error('No link token received from server');
  }

  return linkToken;
};

/**
 * Exchange a public token from Plaid Link for an access token.
 * Also registers the device secret hash for HMAC authentication on future requests.
 *
 * @param publicToken - The public token from Plaid Link callback
 * @param deviceUuid - Optional device UUID for backend connection storage
 * @returns Access token, item ID, and institution name
 */
export const exchangePlaidPublicToken = async (
  publicToken: string,
  deviceUuid?: string,
): Promise<PlaidExchangeResponse> => {
  // Get or create device secret and compute hash
  const { deviceSecretHash } = await getOrCreateDeviceSecret();

  const response = await api.post('/api/plaid/exchange-token/', {
    publicToken: publicToken, // Use camelCase for DRF CamelCaseJSONParser
    deviceUuid: deviceUuid,
    deviceSecretHash: deviceSecretHash, // Register for HMAC auth
  });
  // Backend uses CamelCaseJSONRenderer so response is camelCase
  // Note: access_token is NOT returned for security - it's stored server-side only
  return {
    itemId: response.data.itemId,
    institutionName: response.data.institutionName,
  };
};

/**
 * Fetch investment holdings for a linked Plaid account.
 * Request is signed with HMAC for authentication.
 *
 * @param deviceUuid - The device UUID to look up the connection
 * @returns Holdings data including positions, accounts, and total value
 */
export const getPlaidHoldings = async (deviceUuid: string): Promise<PlaidHoldingsResponse> => {
  const payload = { deviceUuid };

  // Create signed headers for HMAC authentication
  const headers = await createSignedHeaders(deviceUuid, payload);

  const response = await api.post<PlaidHoldingsRawResponse>('/api/plaid/holdings/', payload, {
    headers,
  });

  const data = response.data;
  return {
    holdings: data.holdings.map((h: PlaidHoldingRaw) => ({
      accountId: h.accountId,
      symbol: h.symbol,
      name: h.name,
      type: h.type,
      quantity: h.quantity,
      costBasis: h.costBasis,
      currentValue: h.currentValue,
      price: h.price,
      gainLoss: h.gainLoss,
      gainLossPercent: h.gainLossPercent,
    })),
    accounts: data.accounts.map((a: PlaidAccountRaw) => ({
      accountId: a.accountId,
      name: a.name,
      type: a.type,
      subtype: a.subtype,
      mask: a.mask,
    })),
    totalValue: data.totalValue,
    unsupportedSymbols: data.unsupportedSymbols || [],
  };
};

/**
 * Create a sandbox public token for testing (dev mode only).
 * This bypasses Plaid Link and creates a token directly with investment data.
 *
 * @returns Public token that can be exchanged for access token
 */
export const createSandboxToken = async (): Promise<string> => {
  const response = await api.post('/api/plaid/sandbox-token/');

  if (response.data.error) {
    throw new Error(response.data.error);
  }

  // Handle both snake_case and camelCase responses
  return response.data.public_token || response.data.publicToken;
};

export interface PlaidSnapshot {
  totalValue: number;
  snapshotDate: string;
  holdingsCount: number;
}

export interface PlaidSnapshotsResponse {
  snapshots: PlaidSnapshot[];
  institutionName: string | null;
}

/**
 * Get historical portfolio snapshots from the backend.
 * Request is signed with HMAC for authentication.
 *
 * @param deviceUuid - The device UUID
 * @param days - Number of days of history to fetch (default: 365)
 * @returns List of snapshots and institution name
 */
export const getPlaidSnapshots = async (deviceUuid: string, days = 365): Promise<PlaidSnapshotsResponse> => {
  // Build params - these will be sent as query string
  const params = { deviceUuid, days };

  // For signing, we need to match what the server will compute:
  // - Query params are always strings on the server side
  // - Python json.dumps uses compact separators and sort_keys=True
  // - Pass object to createSignedHeaders so toCompactJson handles sorting
  const paramsForSigning = {
    days: String(days),
    deviceUuid: deviceUuid,
  };

  // Create signed headers (toCompactJson will sort keys alphabetically)
  const headers = await createSignedHeaders(deviceUuid, paramsForSigning);

  const response = await api.get('/api/plaid/snapshots/', {
    params,
    headers,
  });
  return {
    snapshots: response.data.snapshots || [],
    institutionName: response.data.institutionName,
  };
};

/**
 * Disconnect a Plaid connection from the backend.
 * Historical snapshots are preserved for potential future reconnection.
 * Request is signed with HMAC for authentication.
 *
 * @param deviceUuid - The device UUID
 */
export const disconnectPlaid = async (deviceUuid: string): Promise<void> => {
  const payload = { deviceUuid };

  // Create signed headers (toCompactJson will sort keys alphabetically to match Python's sort_keys=True)
  const headers = await createSignedHeaders(deviceUuid, payload);

  await api.post('/api/plaid/disconnect/', payload, { headers });
};
